/**
 * Multi-select countries in a searchable popover (one shared threshold value per row).
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { Checkbox, useModalPortalSurface } from '@/components/ui';
import type { Country } from '@/hooks/smswiser/core/useSMSWiser';
import { cn } from '@/lib/utils';

const TRIGGER_CLASS =
  'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50';

interface CountryMultiPickerProps {
  options: Country[];
  selectedKeys: string[];
  onChangeKeys: (keys: string[]) => void;
  maxKeys: number;
  disabled?: boolean;
}

export const CountryMultiPicker: FC<CountryMultiPickerProps> = ({
  options,
  selectedKeys,
  onChangeKeys,
  maxKeys,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The countries selected when the popover OPENED sit at the top of the
  // list. Snapshotted on open, not live: a country ticked while browsing
  // stays where it is instead of jumping to the top under the cursor.
  const [pinned, setPinned] = useState<Set<string>>(() => new Set());
  const portalSurface = useModalPortalSurface();

  const keySet = useMemo(
    () => new Set(selectedKeys.map((k) => k.trim().toLowerCase())),
    [selectedKeys],
  );

  useEffect(() => {
    if (!open) setSearch('');
    else setPinned(new Set(keySet));
    // Deliberately keyed on `open` only: keySet changes while open must NOT
    // re-pin (see the snapshot note).
  }, [open]);

  const byKey = useMemo(() => {
    const m = new Map<string, Country>();
    for (const c of options) m.set(c.key.trim().toLowerCase(), c);
    return m;
  }, [options]);

  /** Search-filtered, with the countries selected at open time first (in
   *  list order), then the rest. */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const matches = q ? options.filter((c) => c.name.toLowerCase().includes(q)) : options;
    const isPinned = (c: Country) => pinned.has(c.key.trim().toLowerCase());
    return [...matches.filter(isPinned), ...matches.filter((c) => !isPinned(c))];
  }, [options, pinned, search]);

  const toggle = useCallback(
    (rawKey: string) => {
      const k = rawKey.trim().toLowerCase();
      if (keySet.has(k)) {
        onChangeKeys(selectedKeys.filter((x) => x.trim().toLowerCase() !== k));
        return;
      }
      if (selectedKeys.length >= maxKeys) return;
      onChangeKeys([...selectedKeys, k]);
    },
    [keySet, maxKeys, onChangeKeys, selectedKeys],
  );

  const label = useMemo(() => {
    if (selectedKeys.length === 0) return 'Choose countries…';
    const names = selectedKeys
      .map((k) => byKey.get(k.trim().toLowerCase())?.name ?? k)
      .slice(0, 2);
    const extra = selectedKeys.length - names.length;
    return extra > 0 ? `${names.join(', ')} +${extra}` : names.join(', ');
  }, [byKey, selectedKeys]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(TRIGGER_CLASS, selectedKeys.length === 0 && 'text-muted-foreground')}
          aria-label="Countries for this value"
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground', open && 'rotate-180')}
            aria-hidden
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal container={portalSurface ?? undefined}>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-[250] w-[min(100vw-2rem,320px)] rounded-md border border-border bg-popover p-0 text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoComplete="off"
              />
            </div>
          </div>
          <div className="max-h-[240px] overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="py-3 text-center text-xs text-muted-foreground">No matches</p>
            ) : (
              <ul className="space-y-0.5">
                {filtered.map((c) => {
                  const k = c.key.trim().toLowerCase();
                  const checked = keySet.has(k);
                  return (
                    <li key={k}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggle(c.key)}
                          aria-label={c.name}
                        />
                        <span className="min-w-0 truncate">{c.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
